import { useTranslation, Trans } from 'react-i18next';
import React, { useMemo, useCallback, memo } from 'react';
import cx from 'clsx';
import Dropdown from 'antd/es/dropdown';

import { IconRcInfo } from 'assets';
import { Modal } from 'components';
import { useSwitch } from 'hooks';
import { CHAINS_LIST, CHAIN_ID_ALL, CHAINS_DICT } from '@/utils';

import { CurrentChainLabel } from './ChainsMobile';
import style from './index.module.css';

const { CommonModal } = Modal;

type ChainPopoverProps = {
  lableClassName?: string;
  popoverClassName?: string;
  placeholder?: string;
  coloredInput?: boolean;
  includeAll?: boolean;
  error?: boolean;
  disabled?: boolean;
  showAllSupportedChain?: boolean;
  placement?: 'bottomRight' | 'bottomLeft';
} & Pick<
  ChainsPanelProps,
  | 'value'
  | 'onChange'
  | 'assetsDict'
  | 'enabledChains'
  | 'chainsModalSubTitle'
  | 'chainsModalSubTitleClassName'
>;

type ChainsPanelProps = {
  className?: string;
  chainItemStyle?: string;
  onChange: (chain: string) => void;
  value?: string;
  assetsDict?: { [index: string]: number };
  includeAll?: boolean;
  enabledChains?: string[];
  onSupportedClick?(): void;
  chainsModalSubTitle?: string;
  chainsModalSubTitleClassName?: string;
  showAllSupportedChain?: boolean;
};

const ChainsPanel = memo(
  ({
    className,
    onChange,
    value,
    chainItemStyle,
    assetsDict,
    includeAll = true,
    enabledChains,
    showAllSupportedChain = true,
    onSupportedClick,
    chainsModalSubTitle,
    chainsModalSubTitleClassName,
  }: ChainsPanelProps) => {
    const { on, turnOn, turnOff } = useSwitch();
    const validChains = useMemo(() => {
      return assetsDict && Object.entries(assetsDict).filter(([k, v]) => typeof v !== 'undefined');
    }, [assetsDict]);

    const handleSupportedClick = useCallback(() => {
      turnOn();
      onSupportedClick?.();
    }, [turnOn, onSupportedClick]);

    const chains = useMemo(() => {
      const sortedChains = validChains
        ? validChains.sort(([, m], [, n]) => (n || 0) - (m || 0)).map(([x]) => CHAINS_DICT[x])
        : CHAINS_LIST;

      const _chains = enabledChains
        ? sortedChains.filter(x => enabledChains.includes(x.serverId))
        : sortedChains;

      return includeAll ? [CHAIN_ID_ALL, ..._chains] : _chains;
    }, [validChains, includeAll, enabledChains]);

    const handleChainClick = useCallback(
      (e: React.MouseEvent<HTMLDivElement>) => {
        const {
          dataset: { chain },
        } = e.currentTarget;
        onChange?.(chain as string);
      },
      [onChange],
    );

    return (
      <>
        <div className={cx(style.chainPopoverWrap, className)}>
          <div className={style.chainList}>
            {chains.map(chain => (
              <div
                key={chain.serverId}
                data-chain={chain.serverId}
                onClick={handleChainClick}
                className={cx(
                  style.chainItem,
                  chain.serverId === value && style.isSelected,
                  chainItemStyle,
                )}>
                <img className={style.chainIcon} src={(chain as any).logo} alt="" />
                {chain.name}
              </div>
            ))}
          </div>

          {showAllSupportedChain ? (
            <div className={style.chainSupported} onClick={handleSupportedClick}>
              <div>
                <Trans i18nKey="ChainPopover.1ppetwg" count={(enabledChains || CHAINS_LIST).length}>
                  <span className={style.chainCount}>{(enabledChains || CHAINS_LIST).length}</span>
                  chain supported
                </Trans>
              </div>
              <IconRcInfo />
            </div>
          ) : null}
        </div>
        <AllChainsModal
          chainsModalSubTitle={chainsModalSubTitle}
          chainsModalSubTitleClassName={chainsModalSubTitleClassName}
          onRequestClose={turnOff}
          isOpen={on}
          enabledChains={enabledChains}
        />
      </>
    );
  },
);

const AllChainsModal = ({
  onRequestClose,
  isOpen,
  enabledChains,
  chainsModalSubTitleClassName,
  chainsModalSubTitle,
}: {
  isOpen: boolean;
  onRequestClose(): void;
} & Pick<
  ChainsPanelProps,
  'enabledChains' | 'chainsModalSubTitle' | 'chainsModalSubTitleClassName'
>) => {
  const { t } = useTranslation();

  const chains = useMemo(() => {
    return enabledChains?.map(x => CHAINS_DICT[x]).filter(i => !!i) || CHAINS_LIST;
  }, [enabledChains]);

  return (
    <CommonModal
      onRequestClose={onRequestClose}
      on={isOpen}
      size="medium"
      containerClassName={style.allChainsModal}>
      <div className={style.title}>{t('ChainPopover.1orzm4k')}</div>
      <div className={cx(style.subTitle, chainsModalSubTitleClassName)}>
        {chainsModalSubTitle || t('ChainPopover.chainsModalSubTitle')}
      </div>
      <div className={style.list}>
        {chains.map(chain => (
          <div className={style.item} key={chain.id}>
            <img className={style.chainIcon} src={(chain as any).logo} alt="" />
            {chain.name}
          </div>
        ))}
      </div>
    </CommonModal>
  );
};

export const ChainPopover = (props: ChainPopoverProps) => {
  const {
    lableClassName,
    assetsDict,
    placement = 'bottomRight',
    popoverClassName,
    showAllSupportedChain,
    onChange,
    placeholder,
    value,
    coloredInput,
    includeAll,
    error,
    disabled,
    enabledChains,
    chainsModalSubTitle,
    chainsModalSubTitleClassName,
  } = props;

  const { on, turn, turnOff, turnOn } = useSwitch();

  const handleChange = useCallback(
    (chain: string) => {
      onChange(chain);
      turn(false);
    },
    [turn, onChange],
  );

  return (
    <Dropdown
      onVisibleChange={turn}
      visible={on}
      overlay={
        <ChainsPanel
          onSupportedClick={turnOff}
          value={value}
          className={popoverClassName}
          onChange={handleChange}
          assetsDict={assetsDict}
          includeAll={includeAll}
          showAllSupportedChain={showAllSupportedChain}
          enabledChains={enabledChains}
          chainsModalSubTitle={chainsModalSubTitle}
          chainsModalSubTitleClassName={chainsModalSubTitleClassName}
        />
      }
      align={{
        targetOffset: [0, -4],
      }}
      overlayClassName={style.chainsOverlay}
      placement={placement}
      trigger={['click']}>
      <CurrentChainLabel
        value={value}
        colored={coloredInput}
        className={cx(on && style.isOpened, lableClassName)}
        placeholder={placeholder}
        error={error}
        disabled={disabled}
        onClick={turnOn}
      />
    </Dropdown>
  );
};
